/**
 * startChat — open a NEW ChatV2 conversation scoped to a data-library resource.
 *
 * The Data Library's "Start chat" hands us one row — a file OR a folder — and we
 * open `/chat/new` with that row already in focus, so the first question is
 * answered from it without the user opening the `+` menu.
 */

import type { FileOption } from 'src/content/AdvancedSearch/types';
import type { ChatFile } from 'src/content/Chat/types';
import { NEW_CHAT_PATH } from 'src/content/ChatV2/constants';

/**
 * The subset of a data-library row this needs. Deliberately structural rather than
 * importing the Documents `Document` type, which drags the data-library grid models in.
 */
export interface StartChatResource {
  id: number | string;
  type_id: number;
  name?: string;
  display_name?: string;
  type_code?: string;
  format?: string;
  original_format?: string;
  parent_id?: number | null;
  provider_id?: number | null;
}

/** Router-state key carrying the seed. Read by `ConversationView`. */
export const START_CHAT_STATE_KEY = 'chatResources';

/** Router state a "start chat" navigation puts on `/chat/new`. */
export interface StartChatLocationState {
  [START_CHAT_STATE_KEY]?: FileOption[];
}

/**
 * Map a data-library row to the shape the resource pickers and the wire builder already
 * agree on.
 */
export function toChatResourceOption(resource: StartChatResource): FileOption {
  const name = resource.display_name || resource.name || '';
  return {
    author: null,
    created_on: '',
    file_id: Number(resource.id),
    format: resource.format ?? '',
    id: String(resource.id),
    name,
    display_name: name,
    original_format: resource.original_format ?? '',
    provider_id: resource.provider_id ?? null,
    parent_id: resource.parent_id ?? null,
    type_code: resource.type_code ?? '',
    type_id: Number(resource.type_id),
    type_name: ''
  } as FileOption;
}

/**
 * A search result or favourite row (the shared `ChatFile` shape) as a start-chat
 * resource. `file_id` is the resource id; `id` is only a fallback.
 */
export function chatFileToStartChatResource(
  file: ChatFile
): StartChatResource {
  return {
    id: file.file_id ?? file.id,
    type_id: file.type_id,
    name: file.name,
    display_name: file.display_name ?? undefined,
    type_code: file.type_code,
    format: file.format ?? undefined,
    original_format: file.original_format ?? undefined,
    parent_id: file.parent_id,
    provider_id: file.provider_id
  };
}

/**
 * Where to send the browser, and what to carry. Returns the arguments for
 * `navigate` rather than calling it, so callers keep their own analytics and so
 * this stays testable without a router.
 */
export function buildStartChatNavigation(resources: StartChatResource[]): {
  to: string;
  options: { state: StartChatLocationState };
} {
  return {
    to: NEW_CHAT_PATH,
    options: {
      state: { [START_CHAT_STATE_KEY]: resources.map(toChatResourceOption) }
    }
  };
}
